<!--
  RelativeTime.svelte — Displays a date as relative text with a tooltip.

  Shows text like "2 hours ago" or "in 3 days" and reveals the full
  formatted date/time on hover via a flowbite-svelte Tooltip.

  @prop {string | null} date — ISO date string, or null for a fallback label.
  @prop {string} [fallback]  — Text shown when date is null. Default: 'Never'.
-->
<script lang="ts">
	import { Tooltip } from 'flowbite-svelte';
	import { t } from '../i18n';

	
	
	interface Props {
		/** ISO date string to display, or null. */
		date: string | null;
		/** Text shown when the date is null. */
		fallback?: string;
	}

	let { date, fallback }: Props = $props();

	/**
	 * Convert an ISO date string to a human-friendly relative description.
	 * Uses `Intl.RelativeTimeFormat` for locale-aware output.
	 */
	function toRelative(iso: string): string {
		const then = new Date(iso).getTime();
		const now = Date.now();
		const diffSeconds = Math.round((then - now) / 1000);
		const abs = Math.abs(diffSeconds);

		/** Thresholds for each unit (seconds boundary, unit name, divisor). */
		const units: [number, Intl.RelativeTimeFormatUnit, number][] = [
			[60, 'second', 1],
			[3600, 'minute', 60],
			[86400, 'hour', 3600],
			[2592000, 'day', 86400],
			[31536000, 'month', 2592000],
			[Infinity, 'year', 31536000]
		];

		const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
		/** Find the first unit whose threshold exceeds the absolute diff. */
		for (const [threshold, unit, divisor] of units) {
			if (abs < threshold) {
				return rtf.format(Math.round(diffSeconds / divisor), unit);
			}
		}
		return iso;
	}

	/**
	 * Format an ISO date as a full locale date/time string for the tooltip.
	 */
	function toFull(iso: string): string {
		return new Date(iso).toLocaleString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}
</script>

{#if date}
	<span class="cursor-default underline decoration-gray-400 decoration-dotted">
		{toRelative(date)}
	</span>
	<Tooltip>{toFull(date)}</Tooltip>
{:else}
	<span class="text-gray-400 dark:text-gray-500">{fallback ?? $t('Never')}</span>
{/if}
